import { Box } from '@atgfr8/atg-ui-tailwind';
import { SelectInput } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import SectionCard from '@mobileComponents/SectionCard';
import { customerDetailsStore } from '@mobileStore/customer';
import { isFeatureFlagEnabled } from '@mobileStore/featureFlags/store';
import { formatAmount } from '@utils/utils';
import { createEffect, createSignal, Show } from 'solid-js';

import OrderSummary from './components/OrderSummary';
import OutstandingInvoices from './components/OutstandingInvoices';

const TIMEFRAME_OPTIONS = [
  { label: 'Last 12 Months', value: 'Last 12 Month' },
  { label: 'Last 6 Months', value: 'Last 6 Month' },
  { label: 'Last 3 Months', value: 'Last 3 Month' },
  { label: 'Last 1 Month', value: 'Last 1 Month' },
];

const Financials = () => {
  const [financials, setFinancials] = createSignal<{
    availableCredit: string;
    outstandingBalance: string;
    // claimsBalance: string;
    balancePastDue: string;
    avgDaysToPay: string;
    avgProfit: string;
    avgMargin: string;
  }>({
    availableCredit: '$0.00',
    outstandingBalance: '$0.00',
    // claimsBalance: '$0.00',
    balancePastDue: '$0.00',
    avgDaysToPay: '$0.00',
    avgProfit: '$0.00',
    avgMargin: '0.00%',
  });

  const [timeframe, setTimeframe] = createSignal('Last 12 Month');

  createEffect(() => {
    if (customerDetailsStore.customer.financials) {
      const financialsData = customerDetailsStore.customer.financials;
      const selectedTimeframe = timeframe();

      let avgDaysToPay: number | null;
      let avgProfit: number | null;
      let avgMargin: number | null;

      if (selectedTimeframe === 'Last 1 Month') {
        avgDaysToPay = financialsData.avgDaysToPayOneMonth;
        avgProfit = financialsData.avgProfit1M;
        avgMargin = financialsData.avgMargin1M;
      } else if (selectedTimeframe === 'Last 3 Month') {
        avgDaysToPay = financialsData.avgDaysToPayThreeMonths;
        avgProfit = financialsData.avgProfit3M;
        avgMargin = financialsData.avgMargin3M;
      } else if (selectedTimeframe === 'Last 6 Month') {
        avgDaysToPay = financialsData.avgDaysToPaySixMonths;
        avgProfit = financialsData.avgProfit6M;
        avgMargin = financialsData.avgMargin6M;
      } else {
        avgDaysToPay = financialsData.avgDaysToPayTwelveMonths;
        avgProfit = financialsData.avgProfit12M;
        avgMargin = financialsData.avgMargin12M;
      }

      setFinancials({
        availableCredit: formatAmount(financialsData.availableCredit as number),
        outstandingBalance: formatAmount(
          customerDetailsStore.customer.outstandingBalance as number,
        ),
        // claimsBalance: formatAmount(financialsData.openClaimsBalance as number),
        balancePastDue: Boolean(financialsData.balancePastDue)
          ? formatAmount(financialsData.balancePastDue as number)
          : '$0.00',
        avgDaysToPay: String(avgDaysToPay ?? 0),
        avgProfit: formatAmount(avgProfit ?? 0),
        avgMargin: `${((avgMargin ?? 0) * 100).toFixed(2)}%`,
      });
    }
  });

  const FinancialRow = (props: {
    label: string;
    value: string;
    bgColor: string;
  }) => (
    <Box class="mb-3">
      <p class="text-[13px] font-medium text-[#123B50] mb-1">{props.label}</p>
      <Box
        class={`${props.bgColor} px-3 py-1 border border-[#CFD8DC] rounded-sm`}
      >
        <p class="text-[16px] text-[#333]">{props.value}</p>
      </Box>
    </Box>
  );

  return (
    <Box class="p-2">
      <SectionCard title="Financials">
        <Box class="p-2!">
          <FinancialRow
            label="Available Credit"
            value={financials().availableCredit}
            bgColor="bg-[#d6e9c6]"
          />
          <FinancialRow
            label="Outstanding Balance"
            value={financials().outstandingBalance}
            bgColor="bg-[#f8d7da]"
          />
          {/* <FinancialRow
            label="Claims Balance"
            value={financials().claimsBalance}
            bgColor="bg-[#CFD8DC]"
          /> */}
          <FinancialRow
            label="Balance Past Due"
            value={financials().balancePastDue}
            bgColor="bg-[#f8d7da]"
          />
          <Box class="my-3">
            <SelectInput
              label="Timeframe"
              value={timeframe()}
              menuItems={TIMEFRAME_OPTIONS}
              onChange={(value) => setTimeframe(value)}
              dataTestId="customer-financials-timeframe-select"
              class="bg-white text-[14px]"
            />
          </Box>
          <FinancialRow
            label="Average Days to Pay"
            value={financials().avgDaysToPay}
            bgColor="bg-[#CFD8DC]"
          />
          <FinancialRow
            label="Average Profit"
            value={financials().avgProfit}
            bgColor="bg-[#CFD8DC]"
          />
          <FinancialRow
            label="Average Margin"
            value={financials().avgMargin}
            bgColor="bg-[#CFD8DC]"
          />
          <Show when={isFeatureFlagEnabled(FeatureFlags.ViewNegativeCredit)()}>
            <FinancialRow
              label="Negative Credit Limit"
              value={formatAmount(
                customerDetailsStore.customer.negativeCreditAllowance ?? 0,
              )}
              bgColor="bg-[#CFD8DC]"
            />
          </Show>
        </Box>
      </SectionCard>
      <OrderSummary />
      <OutstandingInvoices />
    </Box>
  );
};

export default Financials;
